import { Box, capitalize, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowId } from '@mui/x-data-grid';
import useClientGrid from 'src/careflow/ph-core/grid/hook/useClientGrid';
import { WrappedPlanDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PlanDefinition';
import uuidv4 from 'src/utils/uuidv4';
import { mapActivityDefinitionTask } from 'src/careflow/ph-core/workflow/helpers/workflow';
import moment, { DurationInputArg2 } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridItem, GridSection } from 'src/components/CustomModal';
import CellRow from 'src/careflow/common/CellRow';
import { getOwnerDisplayLabel, TaskSelectedMemberValue } from './TaskModal';
import { getIdFromReference } from 'src/utils/fhir';

export type onTaskSuggestionAction = (taskSuggestion: TaskSuggestion) => void;

export interface TaskSuggestion {
  indexActionTask: string[];
  definitionCanonical: string[];
}

export interface TaskSuggestionsGridProps {
  isTaskSuggestionsOpen: boolean;
  title: string;
  planDefinition: WrappedPlanDefinition | null;
  startDate: moment.Moment | null;
  taskOwner: TaskSelectedMemberValue | null;
  configurations?: any;
  onTaskSuggestionAction: onTaskSuggestionAction;
}

const TaskSuggestionsGrid = ({
  isTaskSuggestionsOpen,
  title,
  planDefinition,
  startDate,
  taskOwner,
  configurations,
  onTaskSuggestionAction
}: TaskSuggestionsGridProps) => {
  const { rowsPerPage, handlePageSizeChange, sortModel, handleSortModelChange } = useClientGrid({
    initialRowsPerPage: 5,
  });
  const [tasksSuggestions, setTasksSuggestions] = useState<any[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getTaskSuggestions = useCallback(async (): Promise<any[]> => {
    if (!planDefinition?.action || !isTaskSuggestionsOpen) {
      setTasksSuggestions([]);
      setRowSelectionModel([]);
      setHasUserInteracted(false);
      return [];
    }

    setIsLoading(true);

    const tasks = await Promise.all(
      planDefinition.action.map(async (action, index) => {
       
        let taskPayload = {
          id: uuidv4(),
          index: String(index),
          definitionCanonical: action?.definitionCanonical,
          endDate: null,
          description: action?.title ?? action?.description,
          status: 'draft',
          owner: null,
          action: action
        };

        if (action?.definitionCanonical) {
          const taskFromActivityDefinition = await mapActivityDefinitionTask(
            getIdFromReference(action.definitionCanonical) ?? '',
            null,
            null,
            true
          );
          taskPayload = {
            ...taskPayload,
            ...taskFromActivityDefinition ?? {}
          };
        }

        return taskPayload;
      })
    );
    setIsLoading(false);
    return tasks;
  }, [isTaskSuggestionsOpen, planDefinition]);

  useEffect(() => {
    const fetchTasks = async () => {
      const suggestions = await getTaskSuggestions();
      setTasksSuggestions(suggestions);
    };
    fetchTasks();
  }, [getTaskSuggestions]);

  useEffect(() => {
    if (!hasUserInteracted && configurations?.allSelected && tasksSuggestions?.length > 0) {
      const tasksSuggestionsIds = tasksSuggestions.map((e) => e.id);
      setRowSelectionModel(tasksSuggestionsIds);

      const taskSuggestionsSelected = tasksSuggestions.reduce(
        (result, obj) => {
          if (obj?.definitionCanonical) {
            result.definitionCanonical.push(obj.definitionCanonical);
          } else {
            result.indexActionTask.push(obj.index);
          }
          return result;
        },
        { indexActionTask: [], definitionCanonical: [] }
      );

      onTaskSuggestionAction(taskSuggestionsSelected);
    }
  }, [tasksSuggestions, configurations?.allSelected, hasUserInteracted]);


  const getSelectedTasksSuggestions = (newSelectionModel) => {
    const newIds = newSelectionModel as string[];

    const newSelectedTasks = tasksSuggestions.filter(task =>
      newIds.includes(task.id)
    );

    const taskSuggestionsSelected = newSelectedTasks.reduce(
      (result, obj) => {
        if (obj?.definitionCanonical) {
          result.definitionCanonical.push(obj.definitionCanonical);
        } else {
          result.indexActionTask.push(obj.index);
        }
        return result;
      },
      { indexActionTask: [], definitionCanonical: [] }
    );

    setHasUserInteracted(true);
    setRowSelectionModel(newIds);
    onTaskSuggestionAction(taskSuggestionsSelected);
  };

  const columns = useMemo(() => 
    getTasksSuggestionsGridColumns(startDate, taskOwner),
  [startDate, taskOwner]);

  return (
    <>
      <GridSection mt={0}>
        <GridItem xs={12} display="flex" alignItems="center">
          <Typography variant="h6">
            {title}
          </Typography>
        </GridItem>
      </GridSection>
      <GridSection mt={1}>
        <GridItem xs={12}>
          <Box sx={{ width: '100%', height: 450 }}>
            <DataGrid
              autoHeight
              getRowHeight={() => 'auto'}
              sx={[
                {
                  marginTop: 1,
                  height: 'auto',
                  width: '100%',
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#f4f6f8',
                  },
                  '& .MuiDataGrid-cell': {
                    border: 0,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    maxHeight: '100px',
                    overflowY: 'auto'
                  },
                  '& .MuiDataGrid-row': {
                    borderBottom: '1px solid #EAECF0',
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#FAFAFA',
                    },
                    '&:hover': {
                      backgroundColor: '#F5F6F7',
                    },
                  },
                },
              ]}
              rows={tasksSuggestions ?? []}
              loading={isLoading}
              density="compact"
              getRowId={({ id }) => id}
              columns={columns}
              disableColumnFilter
              disableColumnMenu
              rowsPerPageOptions={[5, 10, 25]}
              pageSize={rowsPerPage}
              onPageSizeChange={handlePageSizeChange}
              pagination
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              checkboxSelection
              onSelectionModelChange={getSelectedTasksSuggestions}
              selectionModel={rowSelectionModel}
            />
          </Box>
        </GridItem>
      </GridSection>
    </>
  );
};

export const getTasksSuggestionsGridColumns = (
  startDate: moment.Moment | null,
  taskOwner: TaskSelectedMemberValue | null
): GridColDef[] => [
  {
    field: 'description',
    headerName: 'Task',
    flex: 3,
    renderCell: (params) => {
    const { description } = params.row as any;
      return (
        <CellRow
          shouldTruncateText={false}
          title={description ? capitalize(description) : ''}
        />
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.8,
    renderCell: (params) => {
      const { status } = params.row as any;

      return (
        <CellRow
          shouldTruncateText={false}
          title={status ? capitalize(status) : ''}
        />
      );
    },
  },
  {
    field: 'owner',
    headerName: 'Owner',
    flex: 1.6,
    renderCell: (params) => {
    const owner = taskOwner ? getOwnerDisplayLabel(taskOwner) : ''
    return (
      <CellRow
        shouldTruncateText={false}
        title={owner ? capitalize(owner) : ''}
      />
    );
    },
  },
  {
    field: 'endDate',
    headerName: 'Date',
    flex: 0.8,
    renderCell: (params) => {
      const { action } = params.row as any;
      let taskEndDate: moment.Moment | null = null;
      let setStartDate: moment.Moment = startDate ?? moment();
      if (action?.timingDuration?.value && action?.timingDuration?.unit) {
        const isNegativeDuration = action.timingDuration.value < 0;
        if (!isNegativeDuration) {
          taskEndDate = setStartDate
            .clone()
            .add(
              action.timingDuration.value,
              action.timingDuration.unit as DurationInputArg2
            );
        }
      }
      return (
        <CellRow
          shouldTruncateText={false}
          title={taskEndDate ? capitalize(taskEndDate?.format('MM/DD/YYYY')) : ''}
        />
      );
    },
  }
];

export default TaskSuggestionsGrid;
